import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TranslationService } from './translation.service';

export interface ReadRequest {
    functionCode: number; // 3 = Holding Register, 4 = Input Register
    address: number;
    quantity: number;
    cookie?: number;
}

export interface WriteRequest {
    functionCode: number; // 6 = Single, 16 = Multiple
    address: number;
    values: number[];
    cookie?: number;
}

export interface ReadResponse {
    cookie: number;
    values: number[];
}

export interface ReadBatchItem {
    functionCode: number;
    address: number;
    quantity: number;
    cookie: number;
}

export interface ReadBatchRequest {
    reads: ReadBatchItem[];
}

export interface ReadResultItem {
    cookie: number;
    success: boolean;
    values: number[] | null;
    error: string | null;
}

export interface ReadBatchResponse {
    results: ReadResultItem[];
}

export interface WriteResponse {
    cookie: number;
    success: boolean;
}

export interface SessionResponse {
    message: string;
}

export interface FirmwareVersionResponse {
    version: string;
}

/** Shape of a WebGL bundle's manifest.json. */
export interface WebGlManifest {
    runtime: string;
}

export interface DeviceLookupResponse {
    id: number;
    code: string;
    serial: string;
    isOffline: boolean;
    typeId: string | null;
    organizationName: string | null;
    organizationLogoUrl: string | null;
}

export interface UserLanguageResponse {
    languageId: string | null;
}

export interface ErrorResponse {
    error: string;
}

export class ApiError extends Error {
    constructor(
        message: string,
        readonly status: number,
        /** Machine-readable reason from the server, when it sent one. */
        readonly code?: string,
    ) {
        super(message);
    }
}

/** Mirrors SessionConflictCodes on the API. */
export const SESSION_HELD_BY_YOU = 'sessionHeldByYou';

@Injectable({
    providedIn: 'root',
})
export class ApiClientService {
    private readonly _http = inject(HttpClient);
    private readonly _t = inject(TranslationService);
    private readonly _baseUrl = environment.apiUrl;

    private readonly _loading = signal(false);
    readonly loading = this._loading.asReadonly();

    private readonly _sessionActive = signal(false);
    readonly sessionActive = this._sessionActive.asReadonly();

    /** Whether the last device operation reached the traffic light. */
    private readonly _deviceReachable = signal(true);
    readonly deviceReachable = this._deviceReachable.asReadonly();

    /** When a device operation last succeeded. Null until the first one does. */
    private readonly _lastContact = signal<Date | null>(null);
    readonly lastContact = this._lastContact.asReadonly();

    /**
     * Read Modbus registers from device.
     * FunctionCode: 3 = Holding Register, 4 = Input Register
     */
    read(serial: string, request: ReadRequest): Observable<ReadResponse> {
        this._loading.set(true);
        return this._http
            .post<ReadResponse>(`${this._baseUrl}/api/devices/${serial}/read`, request)
            .pipe(
                tap(() => {
                    this._loading.set(false);
                    this.reportContact();
                }),
                catchError((error) => {
                    this._loading.set(false);
                    return throwError(() => this.handleError(error));
                }),
            );
    }

    /**
     * Read multiple registers in one round trip. The server coalesces nearby addresses into
     * as few Modbus transactions as possible and returns a result per requested cookie.
     */
    readBatch(serial: string, request: ReadBatchRequest): Observable<ReadBatchResponse> {
        this._loading.set(true);
        return this._http
            .post<ReadBatchResponse>(`${this._baseUrl}/api/devices/${serial}/read/batch`, request)
            .pipe(
                tap((response) => {
                    this._loading.set(false);
                    this.reportBatchOutcome(response);
                }),
                catchError((error) => {
                    this._loading.set(false);
                    return throwError(() => this.handleError(error));
                }),
            );
    }

    /**
     * Write Modbus registers to device.
     * FunctionCode: 6 = Write Single Holding Register, 16 = Write Multiple Holding Registers
     */
    write(serial: string, request: WriteRequest): Observable<WriteResponse> {
        this._loading.set(true);
        return this._http
            .post<WriteResponse>(`${this._baseUrl}/api/devices/${serial}/write`, request)
            .pipe(
                tap(() => {
                    this._loading.set(false);
                    this.reportContact();
                }),
                catchError((error) => {
                    this._loading.set(false);
                    return throwError(() => this.handleError(error));
                }),
            );
    }

    /**
     * Acquire exclusive session for remote configuration.
     * Pass the SignalR connectionId so the server can release the session when this tab disconnects.
     */
    acquireSession(
        serial: string,
        connectionId?: string | null,
        takeOver = false,
    ): Observable<SessionResponse> {
        const headers = new HttpHeaders(
            connectionId ? { 'X-SignalR-Connection-Id': connectionId } : {},
        );
        return this._http
            .post<SessionResponse>(
                `${this._baseUrl}/api/devices/${serial}/session`,
                {},
                { headers, params: takeOver ? { takeOver: true } : {} },
            )
            .pipe(
                tap(() => this._sessionActive.set(true)),
                catchError((error) => {
                    this._sessionActive.set(false);
                    return throwError(() => this.handleError(error));
                }),
            );
    }

    /**
     * Release session for device.
     */
    releaseSession(serial: string): Observable<SessionResponse> {
        return this._http
            .delete<SessionResponse>(`${this._baseUrl}/api/devices/${serial}/session`)
            .pipe(
                tap(() => this._sessionActive.set(false)),
                catchError((error) => throwError(() => this.handleError(error))),
            );
    }

    /**
     * Read the firmware version from the device's modbus register(s).
     * Requires an active session.
     */
    getFirmwareVersion(serial: string): Observable<FirmwareVersionResponse> {
        return this._http
            .get<FirmwareVersionResponse>(`${this._baseUrl}/api/devices/${serial}/FirmwareVersion`)
            .pipe(
                tap(() => this.reportContact()),
                catchError((error) => throwError(() => this.handleError(error))),
            );
    }

    /**
     * Check whether a WebGL configuration bundle exists for the given firmware version.
     * The bundles are static frontend assets (same origin, not the API), so this probes
     * the bundle manifest directly rather than calling the backend.
     *
     * The manifest is fetched and parsed rather than probed with HEAD: hosts that serve
     * the Angular app with an SPA fallback answer a missing asset with index.html and
     * status 200, so only a manifest that actually parses proves the bundle is shipped.
     */
    webGlVersionAvailable(version: string): Observable<boolean> {
        return this._http.get<WebGlManifest>(`/assets/webgl/v${version}/manifest.json`).pipe(
            map((manifest) => !!manifest?.runtime),
            catchError(() => of(false)),
        );
    }

    /**
     * Look up a device by serial to get its online status.
     */
    lookupDevice(serial: string): Observable<DeviceLookupResponse> {
        return this._http
            .get<DeviceLookupResponse>(`${this._baseUrl}/api/devices/Lookup`, {
                params: { serial },
            })
            .pipe(catchError((error) => throwError(() => this.handleError(error))));
    }

    /**
     * Get the current user's configured language from the Signco API.
     */
    getUserLanguage(): Observable<UserLanguageResponse> {
        return this._http
            .get<UserLanguageResponse>(`${this._baseUrl}/api/users/me/language`)
            .pipe(catchError((error) => throwError(() => this.handleError(error))));
    }

    /** Records that the device answered, clearing any communication failure. */
    private reportContact(): void {
        this._lastContact.set(new Date());
        this._deviceReachable.set(true);
    }

    /**
     * A batch read returns 200 even when the device answered none of its blocks: each block
     * carries its own success flag. Only treat it as contact if something actually came back,
     * otherwise the indicator would report a healthy device that is not answering at all.
     */
    private reportBatchOutcome(response: ReadBatchResponse): void {
        if (response.results?.some((result) => result.success)) {
            this.reportContact();
            return;
        }

        this._deviceReachable.set(false);
    }

    /**
     * Message for a per-block error inside a batch response. These are raw, untranslated server
     * strings, so they are mapped to translated text rather than shown as they arrive.
     */
    deviceErrorMessage(serverError: string | null | undefined): string {
        return serverError?.includes('not responding')
            ? this._t.t('configure.errors.timeout')
            : this._t.t('configure.errors.deviceCommunication');
    }

    private handleError(error: any): ApiError {
        this._loading.set(false);

        // The server's own text is never shown to the user (it is not translated), so keep it
        // in the console for support: a customer screenshot of the console still carries it.
        if (error.error?.error) {
            console.error('[ApiClient] Server error:', error.error.error);
        }

        if (error.status === 0) {
            return new ApiError(this._t.t('configure.errors.connectionFailed'), 0);
        }

        if (error.status === 401) {
            return new ApiError(this._t.t('configure.errors.sessionExpired'), 401);
        }

        if (error.status === 403) {
            return new ApiError(this._t.t('configure.errors.permission'), 403);
        }

        if (error.status === 404) {
            return new ApiError(this._t.t('configure.errors.deviceNotFound'), 404);
        }

        if (error.status === 504 || error.error?.error?.includes('not responding')) {
            this._deviceReachable.set(false);
            return new ApiError(this._t.t('configure.errors.timeout'), 504);
        }

        if (
            (error.status === 400 || error.status === 409) &&
            error.error?.error?.includes('No active session')
        ) {
            this._sessionActive.set(false);
            return new ApiError(this._t.t('configure.errors.noActiveSession'), error.status);
        }

        if (error.status === 409) {
            const code = error.error?.code;
            return code === SESSION_HELD_BY_YOU
                ? new ApiError(this._t.t('configure.errors.sessionHeldByYou'), 409, code)
                : new ApiError(this._t.t('configure.errors.deviceBusy'), 409, code);
        }

        if (error.status === 500) {
            this._deviceReachable.set(false);
            return new ApiError(this._t.t('configure.errors.deviceCommunication'), 500);
        }

        return new ApiError(this._t.t('configure.errors.generic'), error.status ?? 500);
    }
}
